// Client for /api/templates/fill (Task 2) and /api/templates/judge-fill
// (Task 3) — Plan B2 Task 4. Mirrors
// features/chat/services/knowledge-contribution-service.ts's shape: a pure,
// fetch/headers-injectable request core (unit-testable without the store)
// plus a store-connected wrapper the panel calls.
//
// Both endpoints answer 503 when the upstream model isn't configured yet —
// surfaced here as `unavailable: true` so the panel can render the shared
// "AI 서비스가 아직 구성되지 않았어요" advisory instead of an error state. Every
// other failure (auth, malformed output, identity mismatch, transport) is
// `ok:false, unavailable:false` — advisory-only, never blocks manual editing.

import type { Json, SlotConstraint, SlotType } from '@cogo/template'
import { useStudioStore } from '@/store/studio-store'

const CLIENT_TIMEOUT_MS = 30_000

export interface TemplateFillSlotRequest {
  slotId: string
  type: SlotType
  constraint?: SlotConstraint
  sourceValue: Json
}

export interface TemplateFillOutcome {
  ok: boolean
  /** True when the endpoint answered 503 (upstream model not configured) — an advisory "not ready" state, distinct from a transport/parse error. */
  unavailable: boolean
  slotValues: Record<string, Json>
  model: string | null
  error: string | null
}

export interface TemplateJudgeVerdict {
  pass: boolean
  reasons: string[]
}

export interface TemplateJudgeOutcome {
  ok: boolean
  unavailable: boolean
  verdict: TemplateJudgeVerdict | null
  model: string | null
  error: string | null
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

async function postJson(
  path: string,
  body: unknown,
  headers: Record<string, string>,
  fetchFn: typeof fetch,
): Promise<{ status: number; body: Record<string, unknown> }> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS)
  try {
    const response = await fetchFn(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
    let parsed: Record<string, unknown> = {}
    try {
      parsed = readRecord(await response.json())
    } catch {
      parsed = {}
    }
    return { status: response.status, body: parsed }
  } finally {
    clearTimeout(timeout)
  }
}

/** Pure request core — exported for unit tests. `fetchFn`/`headers` are injectable. */
export async function requestTemplateFill(
  request: { templateId: string; brief: string; slots: TemplateFillSlotRequest[] },
  headers: Record<string, string>,
  fetchFn: typeof fetch = fetch,
): Promise<TemplateFillOutcome> {
  try {
    const { status, body } = await postJson('/api/templates/fill', request, headers, fetchFn)
    if (status === 503) {
      return { ok: false, unavailable: true, slotValues: {}, model: null, error: typeof body.error === 'string' ? body.error : 'unavailable' }
    }
    if (body.ok !== true) {
      return { ok: false, unavailable: false, slotValues: {}, model: null, error: typeof body.error === 'string' ? body.error : `http-${status}` }
    }
    return {
      ok: true,
      unavailable: false,
      slotValues: readRecord(body.slotValues) as Record<string, Json>,
      model: typeof body.model === 'string' ? body.model : null,
      error: null,
    }
  } catch (err) {
    return { ok: false, unavailable: false, slotValues: {}, model: null, error: err instanceof Error ? err.message : 'transport' }
  }
}

/** Pure request core — exported for unit tests. */
export async function requestTemplateJudgeFill(
  request: { templateId: string; brief: string; slotValues: Record<string, Json> },
  headers: Record<string, string>,
  fetchFn: typeof fetch = fetch,
): Promise<TemplateJudgeOutcome> {
  try {
    const { status, body } = await postJson('/api/templates/judge-fill', request, headers, fetchFn)
    if (status === 503) {
      return { ok: false, unavailable: true, verdict: null, model: null, error: typeof body.error === 'string' ? body.error : 'unavailable' }
    }
    const verdictRaw = readRecord(body.verdict)
    if (body.ok !== true || typeof verdictRaw.pass !== 'boolean') {
      return { ok: false, unavailable: false, verdict: null, model: null, error: typeof body.error === 'string' ? body.error : `http-${status}` }
    }
    const reasons = Array.isArray(verdictRaw.reasons) ? verdictRaw.reasons.filter((r): r is string => typeof r === 'string') : []
    return {
      ok: true,
      unavailable: false,
      verdict: { pass: verdictRaw.pass, reasons },
      model: typeof body.model === 'string' ? body.model : null,
      error: null,
    }
  } catch (err) {
    return { ok: false, unavailable: false, verdict: null, model: null, error: err instanceof Error ? err.message : 'transport' }
  }
}

function studioAuthHeaders(): Record<string, string> | null {
  const session = useStudioStore.getState().session
  const token = session?.access_token?.trim()
  if (!token) return null
  const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim()
  return {
    Authorization: `Bearer ${token}`,
    ...(anonKey ? { apikey: anonKey } : {}),
  }
}

// --- Batched fill (full-template fills, 100+ slots) ---------------------
//
// /api/templates/fill has a 10s server model budget (FILL_TIMEOUT_MS,
// functions/api/templates/fill.ts). Measured on prod: 10 slots=4.8s OK,
// 25=10.2s edge, 45+=timeout ("fill-unavailable"). TEMPLATE_FILL_BATCH_SIZE
// keeps each request safely under the 25-slot edge. Batches run
// SEQUENTIALLY (deterministic, gentle on the model host) via the existing
// single-request `requestTemplateFill` — nothing about that function's own
// contract changes.

/** Slots per /api/templates/fill request — safe margin under the measured 25-slot/10s edge. */
export const TEMPLATE_FILL_BATCH_SIZE = 15

export interface TemplateFillBatchOutcome {
  slotValues: Record<string, Json>
  /** slotIds whose batch failed (transport error, non-ok response, or unavailable). Batches that never ran because of a `shouldAbort` bail are NOT included here — the whole outcome is expected to be discarded by a caller that aborted. */
  failedSlotIds: string[]
  /** True only when every batch that ran failed specifically because the upstream model isn't configured (503) — the panel keeps today's "AI service not configured" notice only in that all-unavailable case. */
  unavailable: boolean
}

export interface TemplateFillBatchOptions {
  /** Defaults to TEMPLATE_FILL_BATCH_SIZE — override only for tests. */
  batchSize?: number
  /** Called right before a batch's request goes out (1-indexed batchIndex, out of totalBatches). Not called at all when there's only a single batch, so a ≤batchSize fill behaves exactly as before (no progress UI). */
  onBatchStart?: (batchIndex: number, totalBatches: number) => void
  /** Checked before starting each batch (including the first). Returning true stops the loop immediately — no further requests are made, and any batches not yet run contribute nothing to the returned outcome. Lets a newer fill run cut an older, still-in-flight one short. */
  shouldAbort?: () => boolean
}

function chunkSlots(slots: TemplateFillSlotRequest[], batchSize: number): TemplateFillSlotRequest[][] {
  const batches: TemplateFillSlotRequest[][] = []
  for (let i = 0; i < slots.length; i += batchSize) {
    batches.push(slots.slice(i, i + batchSize))
  }
  return batches
}

/** Pure batched request core — exported for unit tests. Reuses `requestTemplateFill` per batch. */
export async function requestTemplateFillBatched(
  request: { templateId: string; brief: string; slots: TemplateFillSlotRequest[] },
  headers: Record<string, string>,
  fetchFn: typeof fetch = fetch,
  options: TemplateFillBatchOptions = {},
): Promise<TemplateFillBatchOutcome> {
  const batchSize = options.batchSize ?? TEMPLATE_FILL_BATCH_SIZE
  const batches = chunkSlots(request.slots, batchSize)
  const slotValues: Record<string, Json> = {}
  const failedSlotIds: string[] = []
  let ranCount = 0
  let unavailableCount = 0

  for (let i = 0; i < batches.length; i++) {
    if (options.shouldAbort?.()) break
    if (batches.length > 1) options.onBatchStart?.(i + 1, batches.length)
    const batch = batches[i]
    ranCount += 1
    const outcome = await requestTemplateFill({ templateId: request.templateId, brief: request.brief, slots: batch }, headers, fetchFn)
    if (outcome.ok) {
      Object.assign(slotValues, outcome.slotValues)
    } else {
      for (const slot of batch) failedSlotIds.push(slot.slotId)
      if (outcome.unavailable) unavailableCount += 1
    }
  }

  return { slotValues, failedSlotIds, unavailable: ranCount > 0 && unavailableCount === ranCount }
}

/** Store-connected wrapper used by TemplateContentPanel for full-template (100+ slot) fills. */
export function fillTemplateSlotsBatched(
  templateId: string,
  brief: string,
  slots: TemplateFillSlotRequest[],
  options: TemplateFillBatchOptions = {},
): Promise<TemplateFillBatchOutcome> {
  const headers = studioAuthHeaders()
  if (!headers) {
    return Promise.resolve({ slotValues: {}, failedSlotIds: slots.map((s) => s.slotId), unavailable: false })
  }
  return requestTemplateFillBatched({ templateId, brief, slots }, headers, fetch, options)
}

/** Store-connected wrapper used by TemplateContentPanel. */
export function judgeTemplateFill(
  templateId: string,
  brief: string,
  slotValues: Record<string, Json>,
): Promise<TemplateJudgeOutcome> {
  const headers = studioAuthHeaders()
  if (!headers) {
    return Promise.resolve({ ok: false, unavailable: false, verdict: null, model: null, error: 'unauthenticated' })
  }
  return requestTemplateJudgeFill({ templateId, brief, slotValues }, headers)
}
